import { type Chrono, ChronoType } from "@modules/chrono/chronoTypes"
import { z } from "zod"

const ChronoForTimeDtoSchema = z.object({
  type: z.literal(ChronoType.FOR_TIME),
  duration: z.number(),
  setsNumber: z.number(),
  restBetweenSets: z.number(),
  isTimeLimitless: z.boolean(),
})

export type ChronoForTimeDto = z.infer<typeof ChronoForTimeDtoSchema>

const ChronoAmrapDtoSchema = z.object({
  type: z.literal(ChronoType.AMRAP),
  duration: z.number(),
  additionalSets: z.array(
    z.object({
      rest: z.number(),
      duration: z.number(),
    }),
  ),
})

export type ChronoAmrapDto = z.infer<typeof ChronoAmrapDtoSchema>

const ChronoEnomDtoSchema = z.object({
  type: z.literal(ChronoType.EMOM),
  duration: z.number(),
  rounds: z.number(),
  setsNumber: z.number(),
  restBetweenSets: z.number(),
})

export type ChronoEnomDto = z.infer<typeof ChronoEnomDtoSchema>

const ChronoTabataDtoSchema = z.object({
  type: z.literal(ChronoType.TABATA),
  rounds: z.number(),
  setsNumber: z.number(),
  restBetweenSets: z.number(),
  duration: z.number(),
  rest: z.number(),
})

export type ChronoTabataDto = z.infer<typeof ChronoTabataDtoSchema>

export const ChronoDtoSchema = z
  .union([ChronoForTimeDtoSchema, ChronoAmrapDtoSchema, ChronoEnomDtoSchema, ChronoTabataDtoSchema])
  .meta({ id: "ChronoDtoSchema" })
export type ChronoDto = z.infer<typeof ChronoDtoSchema>

export function convertChronoFromDto(dto: ChronoDto): Chrono | null {
  if (dto.type === ChronoType.FOR_TIME) {
    return {
      type: ChronoType.FOR_TIME,
      trainingTime: dto.duration,
      numberOfSets: dto.setsNumber,
      restTime: dto.restBetweenSets,
      noLimit: dto.isTimeLimitless,
    }
  } else if (dto.type === ChronoType.AMRAP) {
    return {
      type: ChronoType.AMRAP,
      trainingTime: [dto.duration, ...dto.additionalSets.map((s) => s.duration)],
      numberOfSets: dto.additionalSets.length + 1,
      restTime: [0, ...dto.additionalSets.map((s) => s.rest)],
    }
  } else if (dto.type === ChronoType.EMOM) {
    return {
      type: ChronoType.EMOM,
      trainingTime: dto.duration,
      numberOfRounds: dto.rounds,
      numberOfSets: dto.setsNumber,
      restBetweenSetsTime: dto.restBetweenSets,
    }
  } else /* if (dto.type === ChronoType.TABATA) */ {
    return {
      type: ChronoType.TABATA,
      trainingTime: dto.duration,
      numberOfRounds: dto.rounds,
      numberOfSets: dto.setsNumber,
      restBetweenSetsTime: dto.restBetweenSets,
      restTime: dto.rest,
    }
  }
}

export function convertChronoToDto(chrono: Chrono): ChronoDto | null {
  if (chrono.type === ChronoType.FOR_TIME) {
    return {
      type: ChronoType.FOR_TIME,
      duration: chrono.trainingTime,
      setsNumber: chrono.numberOfSets,
      restBetweenSets: chrono.restTime,
      isTimeLimitless: chrono.noLimit,
    }
  } else if (chrono.type === ChronoType.AMRAP) {
    const duration = chrono.trainingTime[0]
    const additionalDurationSets = chrono.trainingTime.slice(1)
    const additionalRest = chrono.restTime.slice(1)
    const additionalSets = additionalDurationSets.map((duration, index) => {
      const rest = additionalRest[index]
      return { rest, duration }
    })
    return {
      type: ChronoType.AMRAP,
      duration: duration!,
      additionalSets: additionalSets.map((s) => ({ rest: s.rest!, duration: s.duration })),
    }
  } else if (chrono.type === ChronoType.EMOM) {
    return {
      type: ChronoType.EMOM,
      duration: chrono.trainingTime,
      rounds: chrono.numberOfRounds,
      setsNumber: chrono.numberOfSets,
      restBetweenSets: chrono.restBetweenSetsTime,
    }
  } else /* if (chrono.type === ChronoType.TABATA) */ {
    return {
      type: ChronoType.TABATA,
      duration: chrono.trainingTime,
      rounds: chrono.numberOfRounds,
      setsNumber: chrono.numberOfSets,
      restBetweenSets: chrono.restBetweenSetsTime,
      rest: chrono.restTime,
    }
  }
}
